import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MbCard } from "@/components/MbCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { tAdmin } from "@/lib/i18n";
import { Calendar, Edit, Trash2, Plus, ArrowLeft, HelpCircle, CheckCircle, Play, Image as ImageIcon, X } from "lucide-react";
import logo from "@/assets/logo.jpg";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { optimizeImage, validateImageFile, formatFileSize } from "@/lib/image-optimizer";
// Note: Sessions page doesn't access user profiles, so no audit logging needed
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

interface Session {
  id: string;
  title_gu: string;
  title_hi: string | null;
  title_en: string | null;
  description_gu: string | null;
  description_hi: string | null;
  yt_video_id: string;
  video_language: 'gu' | 'hi';
  date: string;
  windows: any;
  notes: string | null;
  status: string;
  thumbnail_url: string | null;
  thumbnail_active: boolean;
}

export default function AdminSessions() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Form state
  const [titleGu, setTitleGu] = useState("");
  const [titleHi, setTitleHi] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [descriptionGu, setDescriptionGu] = useState("");
  const [descriptionHi, setDescriptionHi] = useState("");
  const [ytVideoId, setYtVideoId] = useState("");
  const [videoLanguage, setVideoLanguage] = useState<'gu' | 'hi'>('gu');
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<"draft" | "published" | "expired">("draft");
  const [hasMorning, setHasMorning] = useState(true);
  const [hasEvening, setHasEvening] = useState(true);
  const [morningFrom, setMorningFrom] = useState("07:00");
  const [morningTo, setMorningTo] = useState("10:00");
  const [eveningFrom, setEveningFrom] = useState("18:00");
  const [eveningTo, setEveningTo] = useState("21:00");
  const [youtubeIdValid, setYoutubeIdValid] = useState(true);
  const [timeWindowErrors, setTimeWindowErrors] = useState({ morning: "", evening: "" });
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewVideoId, setPreviewVideoId] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [optimizingImage, setOptimizingImage] = useState(false);

  useEffect(() => {
    checkAdminAuth();
    fetchSessions();
  }, []);

  const checkAdminAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin");

    if (!roles || roles.length === 0) {
      navigate("/today");
    }
  };

  const fetchSessions = async () => {
    try {
      const { data, error } = await supabase
        .from("sessions")
        .select("*")
        .order("date", { ascending: false });

      if (error) throw error;
      if (data) {
        setSessions(data.map(session => ({
          ...session,
          video_language: (session.video_language as 'gu' | 'hi'),
        })));
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const extractYouTubeId = (url: string): string => {
    // If it's already just the ID (11 chars), return it
    if (url.length === 11 && /^[a-zA-Z0-9_-]+$/.test(url)) {
      return url;
    }
    
    // Extract from various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return url;
  };

  const handleYouTubeIdChange = (value: string) => {
    const extractedId = extractYouTubeId(value);
    setYtVideoId(extractedId);
    setYoutubeIdValid(extractedId.length === 11);
  };

  const validateTimeWindow = (from: string, to: string): boolean => {
    const [fromHour, fromMin] = from.split(':').map(Number);
    const [toHour, toMin] = to.split(':').map(Number);
    const fromMinutes = fromHour * 60 + fromMin;
    const toMinutes = toHour * 60 + toMin;
    return toMinutes > fromMinutes;
  };

  const handleMorningTimeChange = (field: 'from' | 'to', value: string) => {
    if (field === 'from') {
      setMorningFrom(value);
      if (morningTo && !validateTimeWindow(value, morningTo)) {
        setTimeWindowErrors(prev => ({ ...prev, morning: "સમાપ્તિ સમય શરૂઆત સમય પછી હોવો જોઈએ" }));
      } else {
        setTimeWindowErrors(prev => ({ ...prev, morning: "" }));
      }
    } else {
      setMorningTo(value);
      if (morningFrom && !validateTimeWindow(morningFrom, value)) {
        setTimeWindowErrors(prev => ({ ...prev, morning: "સમાપ્તિ સમય શરૂઆત સમય પછી હોવો જોઈએ" }));
      } else {
        setTimeWindowErrors(prev => ({ ...prev, morning: "" }));
      }
    }
  };

  const handleEveningTimeChange = (field: 'from' | 'to', value: string) => {
    if (field === 'from') {
      setEveningFrom(value);
      if (eveningTo && !validateTimeWindow(value, eveningTo)) {
        setTimeWindowErrors(prev => ({ ...prev, evening: "સમાપ્તિ સમય શરૂઆત સમય પછી હોવો જોઈએ" }));
      } else {
        setTimeWindowErrors(prev => ({ ...prev, evening: "" }));
      }
    } else {
      setEveningTo(value);
      if (eveningFrom && !validateTimeWindow(eveningFrom, value)) {
        setTimeWindowErrors(prev => ({ ...prev, evening: "સમાપ્તિ સમય શરૂઆત સમય પછી હોવો જોઈએ" }));
      } else {
        setTimeWindowErrors(prev => ({ ...prev, evening: "" }));
      }
    }
  };

  const resetForm = () => {
    setTitleGu("");
    setTitleHi("");
    setTitleEn("");
    setDescriptionGu("");
    setDescriptionHi("");
    setYtVideoId("");
    setVideoLanguage('gu');
    setDate("");
    setNotes("");
    setStatus("draft");
    setHasMorning(true);
    setHasEvening(true);
    setMorningFrom("07:00");
    setMorningTo("10:00");
    setEveningFrom("18:00");
    setEveningTo("21:00");
    setYoutubeIdValid(true);
    setEditingSession(null);
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setThumbnailUrl(null);
    if (thumbnailPreview) {
      URL.revokeObjectURL(thumbnailPreview);
    }
  };

  const handleEdit = (session: Session) => {
    setEditingSession(session);
    setTitleGu(session.title_gu);
    setTitleHi(session.title_hi || "");
    setTitleEn(session.title_en || "");
    setDescriptionGu(session.description_gu || "");
    setDescriptionHi(session.description_hi || "");
    setYtVideoId(session.yt_video_id);
    setVideoLanguage(session.video_language || 'gu');
    setDate(session.date);
    setNotes(session.notes || "");
    setStatus(session.status as any);
    setThumbnailUrl(session.thumbnail_url);
    setThumbnailPreview(null);
    setThumbnailFile(null);
    
    const windows = session.windows as any[];
    const morningWindow = windows.find((w) => w.label === "morning");
    const eveningWindow = windows.find((w) => w.label === "evening");
    
    setHasMorning(!!morningWindow);
    setHasEvening(!!eveningWindow);
    
    if (morningWindow) {
      setMorningFrom(morningWindow.from);
      setMorningTo(morningWindow.to);
    }
    if (eveningWindow) {
      setEveningFrom(eveningWindow.from);
      setEveningTo(eveningWindow.to);
    }
    
    setYoutubeIdValid(session.yt_video_id.length === 11);
    setDialogOpen(true);
  };

  const handleTranslate = async (field: 'title' | 'description') => {
    const sourceText = field === 'title' ? titleGu : descriptionGu;
    
    if (!sourceText) {
      toast({ 
        title: "Cannot translate", 
        description: `Please fill in the Gujarati ${field} first`, 
        variant: "destructive" 
      });
      return;
    }

    setIsTranslating(true);
    try {
      const { data, error } = await supabase.functions.invoke('translate-content', {
        body: { 
          text: sourceText, 
          sourceLanguage: 'gu', 
          targetLanguage: 'hi' 
        }
      });

      if (error) throw error;
      
      if (data?.translatedText) {
        if (field === 'title') {
          setTitleHi(data.translatedText);
        } else {
          setDescriptionHi(data.translatedText);
        }
        toast({ 
          title: "Translation complete", 
          description: "Hindi content has been auto-generated. Please review before saving." 
        });
      } else {
        throw new Error('No translation received');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast({ 
        title: "Translation failed", 
        description: error instanceof Error ? error.message : "Please try again", 
        variant: "destructive" 
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const handleThumbnailSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast({
        title: "Invalid File",
        description: validation.error,
        variant: "destructive",
      });
      return;
    }

    setOptimizingImage(true);
    try {
      const result = await optimizeImage(file, {
        targetWidth: 1280,
        targetHeight: 720,
        quality: 0.85,
        format: 'webp',
      });

      const previewUrl = URL.createObjectURL(result.blob);
      setThumbnailPreview(previewUrl);
      
      const optimizedFile = new File(
        [result.blob],
        `thumbnail-${Date.now()}.webp`,
        { type: 'image/webp' }
      );
      setThumbnailFile(optimizedFile);

      toast({
        title: "✅ Image Optimized",
        description: `${formatFileSize(result.originalSize)} → ${formatFileSize(result.optimizedSize)} (${result.width}×${result.height})`,
      });
    } catch (error) {
      console.error("Image optimization error:", error);
      toast({
        title: "Optimization Failed",
        description: error instanceof Error ? error.message : "Failed to process image",
        variant: "destructive",
      });
    } finally {
      setOptimizingImage(false);
    }
  };

  const handleRemoveThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setThumbnailUrl(null);
    if (thumbnailPreview) {
      URL.revokeObjectURL(thumbnailPreview);
    }
  };

  const uploadThumbnailToStorage = async (sessionId: string): Promise<string | null> => {
    if (!thumbnailFile) return null;

    setUploadingThumbnail(true);
    try {
      const filePath = `${sessionId}/thumbnail-${Date.now()}.webp`;
      
      const { data, error } = await supabase.storage
        .from('session-thumbnails')
        .upload(filePath, thumbnailFile, {
          cacheControl: '3600',
          upsert: true,
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('session-thumbnails')
        .getPublicUrl(data.path);

      return publicUrl;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSave = async () => {
    // Validate required fields based on video language
    if (!date) {
      toast({
        title: tAdmin.admin.session.error.failed,
        description: tAdmin.admin.session.error.required,
        variant: "destructive",
      });
      return;
    }

    if (videoLanguage === 'gu' && (!titleGu || !descriptionGu)) {
      toast({
        title: tAdmin.admin.session.error.failed,
        description: "Please fill in Gujarati title and description",
        variant: "destructive",
      });
      return;
    }

    if (videoLanguage === 'hi' && (!titleHi || !descriptionHi)) {
      toast({
        title: tAdmin.admin.session.error.failed,
        description: "Please fill in Hindi title and description",
        variant: "destructive",
      });
      return;
    }

    if (ytVideoId && ytVideoId.length !== 11) {
      toast({
        title: tAdmin.admin.session.error.failed,
        description: tAdmin.admin.session.error.invalidYoutubeId,
        variant: "destructive",
      });
      return;
    }

    if (hasMorning && !validateTimeWindow(morningFrom, morningTo)) {
      toast({
        title: tAdmin.admin.session.error.failed,
        description: "સવારનો સમાપ્તિ સમય શરૂઆત સમય પછી હોવો જોઈએ",
        variant: "destructive",
      });
      return;
    }

    if (hasEvening && !validateTimeWindow(eveningFrom, eveningTo)) {
      toast({
        title: tAdmin.admin.session.error.failed,
        description: "સાંજનો સમાપ્તિ સમય શરૂઆત સમય પછી હોવો જોઈએ",
        variant: "destructive",
      });
      return;
    }


    const windows = [];
    if (hasMorning) windows.push({ label: "morning", from: morningFrom, to: morningTo });
    if (hasEvening) windows.push({ label: "evening", from: eveningFrom, to: eveningTo });

    try {
      let uploadedThumbnailUrl = thumbnailUrl;

      if (thumbnailFile) {
        const tempId = editingSession?.id || crypto.randomUUID();
        uploadedThumbnailUrl = await uploadThumbnailToStorage(tempId);
      }

      const sessionData = {
        title_gu: titleGu,
        title_hi: titleHi || null,
        title_en: titleEn || null,
        description_gu: descriptionGu || null,
        description_hi: descriptionHi || null,
        yt_video_id: ytVideoId || '',
        video_language: videoLanguage,
        date,
        windows,
        notes: notes || null,
        status,
        thumbnail_url: uploadedThumbnailUrl,
      };

      if (editingSession) {
        const { error } = await supabase
          .from("sessions")
          .update(sessionData)
          .eq("id", editingSession.id);

        if (error) throw error;
        toast({ title: tAdmin.admin.session.success.updated });
      } else {
        const { error } = await supabase.from("sessions").insert(sessionData);

        if (error) throw error;
        toast({ title: tAdmin.admin.session.success.created });
      }

      setDialogOpen(false);
      resetForm();
      fetchSessions();
    } catch (error: any) {
      toast({
        title: tAdmin.admin.session.error.failed,
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(tAdmin.admin.session.confirm.delete)) return;

    try {
      const { error } = await supabase.from("sessions").delete().eq("id", id);

      if (error) throw error;

      toast({ title: tAdmin.admin.session.success.deleted });
      fetchSessions();
    } catch (error: any) {
      toast({
        title: tAdmin.admin.session.error.failed,
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">{tAdmin.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))]">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="દમયંતી બાગ" className="h-10" />
              <h1 className="text-xl font-bold text-primary">{tAdmin.admin.sessions}</h1>
            </div>
            <Button variant="ghost" onClick={() => navigate("/admin")}>
              <ArrowLeft className="mr-2 w-4 h-4" />
              {tAdmin.admin.backToDashboard}
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-6">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="hero" onClick={resetForm}>
              <Plus className="mr-2 w-4 h-4" />
              {tAdmin.admin.session.create}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingSession ? tAdmin.admin.session.edit : tAdmin.admin.session.create}
              </DialogTitle>
            </DialogHeader>
            <TooltipProvider>
              <div className="space-y-4">
                {/* Video Language - FIRST FIELD */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label>વિડિયોની ભાષા / Video Language <span className="text-destructive">*</span></Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Select the language spoken in the video. This determines which content fields you need to fill.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Select value={videoLanguage} onValueChange={(value: 'gu' | 'hi') => setVideoLanguage(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gu">ગુજરાતી (Gujarati)</SelectItem>
                      <SelectItem value="hi">हिंदी (Hindi)</SelectItem>
                    </SelectContent>
                  </Select>
                  {videoLanguage === 'hi' && (
                    <p className="text-sm text-amber-600 mt-2">
                      ℹ️ For Hindi videos, you must provide Hindi title and description below.
                    </p>
                  )}
                </div>

                {/* Gujarati Content - Show only if Gujarati video */}
                {videoLanguage === 'gu' && (
                  <>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Label>શીર્ષક (ગુજરાતી) <span className="text-destructive">*</span></Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Title in Gujarati - This will be shown to users</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Input
                        value={titleGu}
                        onChange={(e) => setTitleGu(e.target.value)}
                        placeholder="જૈવિક ખેતી: જમીનને તંદુરસ્ત રાખવા"
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Label>વર્ણન (ગુજરાતી) <span className="text-destructive">*</span></Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Description in Gujarati - This will be shown to users</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Textarea
                        value={descriptionGu}
                        onChange={(e) => setDescriptionGu(e.target.value)}
                        placeholder="વૈદિક ઉપાયો અને આધુનિક પદ્ધતિઓ"
                        className="min-h-[100px]"
                      />
                    </div>
                  </>
                )}

                {/* Hindi Content - Show only if Hindi video */}
                {videoLanguage === 'hi' && (
                  <>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Label>शीर्षक (हिंदी) <span className="text-destructive">*</span></Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Title in Hindi - This will be shown to users</p>
                          </TooltipContent>
                        </Tooltip>
                        {titleGu && !titleHi && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleTranslate('title')}
                            disabled={isTranslating}
                            className="ml-auto"
                          >
                            {isTranslating ? "Translating..." : "🪄 Auto-translate from Gujarati"}
                          </Button>
                        )}
                      </div>
                      <Input
                        value={titleHi}
                        onChange={(e) => setTitleHi(e.target.value)}
                        placeholder="शीर्षक दर्ज करें"
                      />
                      {!titleGu && (
                        <p className="text-xs text-muted-foreground mt-1">
                          💡 Tip: First add Gujarati title above, then use auto-translate
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Label>विवरण (हिंदी) <span className="text-destructive">*</span></Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Description in Hindi - This will be shown to users</p>
                          </TooltipContent>
                        </Tooltip>
                        {descriptionGu && !descriptionHi && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleTranslate('description')}
                            disabled={isTranslating}
                            className="ml-auto"
                          >
                            {isTranslating ? "Translating..." : "🪄 Auto-translate from Gujarati"}
                          </Button>
                        )}
                      </div>
                      <Textarea
                        value={descriptionHi}
                        onChange={(e) => setDescriptionHi(e.target.value)}
                        placeholder="विवरण दर्ज करें"
                        className="min-h-[100px]"
                      />
                      {!descriptionGu && (
                        <p className="text-xs text-muted-foreground mt-1">
                          💡 Tip: First add Gujarati description above, then use auto-translate
                        </p>
                      )}
                    </div>

                    {/* Helper fields for translation source */}
                    <div className="bg-muted/50 p-4 rounded-md space-y-3">
                      <p className="text-sm font-medium">Translation Helper (Gujarati Source)</p>
                      <div>
                        <Label className="text-xs text-muted-foreground">Gujarati Title (for reference)</Label>
                        <Input
                          value={titleGu}
                          onChange={(e) => setTitleGu(e.target.value)}
                          placeholder="ગુજરાતીમાં શીર્ષક"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Gujarati Description (for reference)</Label>
                        <Textarea
                          value={descriptionGu}
                          onChange={(e) => setDescriptionGu(e.target.value)}
                          placeholder="ગુજરાતીમાં વર્ણન"
                          className="min-h-[80px] mt-1"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* English Title - Always show (for SEO) */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label>Title (English)</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Optional - English title for SEO and reference</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    value={titleEn}
                    onChange={(e) => setTitleEn(e.target.value)}
                    placeholder="Enter title in English (optional)"
                  />
                </div>

                {/* Thumbnail Upload */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label>Thumbnail Image (Optional)</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Upload a custom 16:9 thumbnail (1280×720px). Large files will be auto-resized and optimized. Shows as "Coming Soon" teaser when video isn't uploaded yet.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  {thumbnailPreview || thumbnailUrl ? (
                    <div className="space-y-2">
                      <div className="relative aspect-video rounded-lg overflow-hidden border-2 border-border">
                        <img
                          src={thumbnailPreview || thumbnailUrl || ''}
                          alt="Thumbnail preview"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-2 right-2">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={handleRemoveThumbnail}
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        {!ytVideoId && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <div className="text-white text-center p-4">
                              <p className="text-lg font-bold">🎥 Upcoming Session</p>
                              <p className="text-sm">Stay Tuned</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {ytVideoId ? '⚠️ Video URL present - thumbnail hidden in public view' : '✅ Thumbnail will display as "Coming Soon"'}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleThumbnailSelect}
                        disabled={optimizingImage}
                      />
                      {optimizingImage && (
                        <p className="text-xs text-muted-foreground mt-2">
                          🔄 Optimizing image...
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Accepts: JPG, PNG, WebP • Max: 10MB • Auto-resized to 1280×720px
                      </p>
                    </div>
                  )}
                </div>

                {/* YouTube Video ID */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label>{tAdmin.admin.session.youtubeId} (Optional if thumbnail provided)</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        <div className="space-y-2">
                          <p className="font-semibold">{tAdmin.admin.session.youtube.helper}</p>
                          <div className="text-xs space-y-1">
                            <p>• {tAdmin.admin.session.youtube.format1}<code className="bg-muted px-1">VIDEO_ID</code></p>
                            <p>• {tAdmin.admin.session.youtube.format2}<code className="bg-muted px-1">VIDEO_ID</code></p>
                            <p>• {tAdmin.admin.session.youtube.format3}<code className="bg-muted px-1">VIDEO_ID</code></p>
                            <p className="mt-2">{tAdmin.admin.session.youtube.example}</p>
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="space-y-2">
                    <Input
                      value={ytVideoId}
                      onChange={(e) => handleYouTubeIdChange(e.target.value)}
                      placeholder="Paste YouTube URL or Video ID"
                      className={!youtubeIdValid ? "border-destructive" : ""}
                    />
                    {ytVideoId && (
                      <div className="flex items-center gap-2 text-sm">
                        {youtubeIdValid ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-green-600">Valid YouTube ID: {ytVideoId}</span>
                          </>
                        ) : (
                          <span className="text-destructive">Invalid - must be 11 characters</span>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      💡 {tAdmin.admin.session.youtube.pasteUrl}
                    </p>
                  </div>
                </div>

                {/* Video Preview */}
                {ytVideoId && youtubeIdValid && (
                  <div className="space-y-2">
                    <Label>Video Preview</Label>
                    <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                      <img
                        src={`https://img.youtube.com/vi/${ytVideoId}/maxresdefault.jpg`}
                        alt="Video thumbnail"
                        className="w-full h-full object-cover"
                      />
                      <Button
                        type="button"
                        variant="hero"
                        size="sm"
                        className="absolute bottom-4 right-4"
                        onClick={() => {
                          setPreviewVideoId(ytVideoId);
                          setPreviewDialogOpen(true);
                        }}
                      >
                        <Play className="mr-2 w-4 h-4" />
                        Preview Video
                      </Button>
                    </div>
                  </div>
                )}

                {/* Date */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label>{tAdmin.admin.session.date}</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{tAdmin.admin.session.tooltips.date}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>

                {/* Time Windows */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label>{tAdmin.admin.session.windows}</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{tAdmin.admin.session.tooltips.windows}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="space-y-3 mt-2 border rounded-md p-4">
                    {/* Morning Window */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={hasMorning}
                          onCheckedChange={(checked) => setHasMorning(checked as boolean)}
                        />
                        <span className="font-medium">{tAdmin.admin.session.morning}</span>
                      </div>
                      {hasMorning && (
                        <div className="ml-6 space-y-2">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">{tAdmin.admin.session.from}</Label>
                            <Input
                              type="time"
                              value={morningFrom}
                              onChange={(e) => handleMorningTimeChange('from', e.target.value)}
                              className={`w-32 ${timeWindowErrors.morning ? 'border-destructive' : ''}`}
                            />
                            <Label className="text-xs">{tAdmin.admin.session.to}</Label>
                            <Input
                              type="time"
                              value={morningTo}
                              onChange={(e) => handleMorningTimeChange('to', e.target.value)}
                              className={`w-32 ${timeWindowErrors.morning ? 'border-destructive' : ''}`}
                            />
                          </div>
                          {timeWindowErrors.morning && (
                            <p className="text-xs text-destructive ml-6">{timeWindowErrors.morning}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Evening Window */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={hasEvening}
                          onCheckedChange={(checked) => setHasEvening(checked as boolean)}
                        />
                        <span className="font-medium">{tAdmin.admin.session.evening}</span>
                      </div>
                      {hasEvening && (
                        <div className="ml-6 space-y-2">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">{tAdmin.admin.session.from}</Label>
                            <Input
                              type="time"
                              value={eveningFrom}
                              onChange={(e) => handleEveningTimeChange('from', e.target.value)}
                              className={`w-32 ${timeWindowErrors.evening ? 'border-destructive' : ''}`}
                            />
                            <Label className="text-xs">{tAdmin.admin.session.to}</Label>
                            <Input
                              type="time"
                              value={eveningTo}
                              onChange={(e) => handleEveningTimeChange('to', e.target.value)}
                              className={`w-32 ${timeWindowErrors.evening ? 'border-destructive' : ''}`}
                            />
                          </div>
                          {timeWindowErrors.evening && (
                            <p className="text-xs text-destructive ml-6">{timeWindowErrors.evening}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label>{tAdmin.admin.session.notes}</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{tAdmin.admin.session.tooltips.notes}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Internal admin notes..."
                    rows={2}
                  />
                </div>

                {/* Status */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label>{tAdmin.admin.session.status}</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{tAdmin.admin.session.tooltips.status}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Select value={status} onValueChange={(val: any) => setStatus(val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">{tAdmin.admin.session.draft}</SelectItem>
                      <SelectItem value="published">{tAdmin.admin.session.published}</SelectItem>
                      <SelectItem value="expired">{tAdmin.admin.session.expired}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button variant="hero" onClick={handleSave} className="flex-1">
                    {tAdmin.save}
                  </Button>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    {tAdmin.cancel}
                  </Button>
                </div>
              </div>
            </TooltipProvider>
          </DialogContent>
        </Dialog>

        {/* Sessions List */}
        <div className="space-y-4">
          {sessions.map((session) => (
            <MbCard key={session.id} elevated className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      session.video_language === 'hi' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {session.video_language === 'hi' ? 'हिंदी' : 'ગુજરાતી'}
                    </span>
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {new Date(session.date).toLocaleDateString("gu-IN")}
                    </span>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        session.status === "published"
                          ? "bg-primary/20 text-primary"
                          : session.status === "draft"
                          ? "bg-muted text-muted-foreground"
                          : "bg-destructive/20 text-destructive"
                      }`}
                    >
                      {session.status}
                    </span>
                  </div>
                  <h3 className="font-bold text-lg mb-1">
                    {session.video_language === 'hi' 
                      ? (session.title_hi || session.title_gu)
                      : session.title_gu
                    }
                    {session.video_language === 'hi' && !session.title_hi && (
                      <span className="ml-2 text-xs text-amber-600 font-normal">(using Gujarati fallback)</span>
                    )}
                  </h3>
                  {((session.video_language === 'hi' ? session.description_hi : session.description_gu) || session.description_gu) && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {session.video_language === 'hi' 
                        ? (session.description_hi || session.description_gu)
                        : session.description_gu
                      }
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(session)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(session.id)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </MbCard>
          ))}
        </div>
      </div>

      {/* Video Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Video Preview</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${previewVideoId}`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
